import React from 'react';
import { Layout, Menu, Button } from 'antd';
import type { MenuProps } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../features/auth/useAuth';
import { PlusLogo } from './PlusLogo';

const { Sider, Content } = Layout;

type LeafItem = { key: string; label: string };
type GroupItem = { key: string; label: string; children: LeafItem[] };
type NavItem = LeafItem | GroupItem;

const NAV_ITEMS: NavItem[] = [
  { key: '/patients',    label: 'Пациенты' },
  { key: '/prostheses',  label: 'Типы протезов' },
  {
    key: 'group:testing',
    label: 'Тестирование',
    children: [
      { key: '/today-tasks', label: 'Задачи на сегодня' },
      { key: '/scheduler',   label: 'Планировщик' },
      { key: '/operations',  label: 'Операции' },
    ],
  },
  {
    key: 'group:lfk',
    label: 'ЛФК',
    children: [
      { key: '/exercises',    label: 'Упражнения' },
      { key: '/lfk-programs', label: 'Программы ЛФК' },
    ],
  },
  {
    key: 'group:pain',
    label: 'Обезболивание',
    children: [
      { key: '/medications',   label: 'Лекарства' },
      { key: '/pain-programs', label: 'Программы обезболивания' },
    ],
  },
  {
    key: 'group:surveys',
    label: 'Опросы',
    children: [
      { key: '/surveys',       label: 'Разделы' },
      { key: '/checkpoints',   label: 'Сроки' },
      { key: '/survey-matrix', label: 'Матрица анкет' },
    ],
  },
  {
    key: 'group:diary',
    label: 'Дневник боли',
    children: [
      { key: '/diary-triggers', label: 'Триггеры дневника боли' },
    ],
  },
  {
    key: 'group:settings',
    label: 'Настройки',
    children: [
      { key: '/settings/general', label: 'Общие настройки' },
      { key: '/settings/notifications', label: 'Настройки уведомлений' },
    ],
  },
];

const isGroup = (item: NavItem): item is GroupItem => 'children' in item;

function findSelected(pathname: string): { selectedKey: string; parentKey?: string } {
  for (const item of NAV_ITEMS) {
    if (isGroup(item)) {
      const child = item.children.find(c => pathname.startsWith(c.key));
      if (child) return { selectedKey: child.key, parentKey: item.key };
    } else if (pathname.startsWith(item.key)) {
      return { selectedKey: item.key };
    }
  }
  return { selectedKey: '/patients' };
}

export function AppLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const { selectedKey, parentKey } = findSelected(pathname);
  // Accordion: только одна группа раскрыта одновременно, чтобы нижние
  // пункты меню не уезжали за пределы видимой области.
  const [openKeys, setOpenKeys] = React.useState<string[]>(parentKey ? [parentKey] : []);

  React.useEffect(() => {
    if (parentKey) {
      setOpenKeys([parentKey]);
    }
  }, [parentKey]);

  const handleOpenChange: MenuProps['onOpenChange'] = keys => {
    const latestKey = (keys as string[]).find(k => !openKeys.includes(k));
    setOpenKeys(latestKey ? [latestKey] : []);
  };

  const items: MenuProps['items'] = NAV_ITEMS.map(item =>
    isGroup(item)
      ? { key: item.key, label: item.label, children: item.children.map(c => ({ key: c.key, label: c.label })) }
      : { key: item.key, label: item.label },
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={220}
        theme="light"
        style={{
          borderRight: '1px solid #f0f0f0',
          height: '100vh',
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div
            style={{
              padding: '16px 24px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <PlusLogo width={96} />
          </div>
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            items={items}
            onClick={({ key }) => {
              if (key.startsWith('/')) navigate(key);
            }}
            style={{ borderRight: 'none', flex: 1, minHeight: 0, overflowY: 'auto' }}
          />
          <div style={{ marginTop: 'auto', borderTop: '1px solid #f0f0f0' }}>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              block
              style={{ height: 40, textAlign: 'left', paddingLeft: 24, borderRadius: 0 }}
            >
              Выйти
            </Button>
          </div>
        </div>
      </Sider>
      <Layout>
        <Content style={{ padding: 24, background: '#fff' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
